/**
 * Diagnostic Logs (feature guide)
 *
 * Screenshots were taken from the Sokuji v0.41.1 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Diagnostic Logs",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "Diagnostic logs record what Sokuji and the translation service exchange during a session. They help us find the cause when something goes wrong. They are off by default."
  },
  "steps": [
    {
      "title": "Switch them on",
      "content": "In Settings, under <strong>Help</strong>, switch on <strong>Diagnostic logs</strong>. They stay on after a restart until you switch them off.",
      "screenshot": "01-diagnostic-logs-switch.png"
    },
    {
      "title": "Open the Logs panel",
      "content": "A <strong>Logs</strong> button appears in the title bar.",
      "screenshot": "02-logs-button.png"
    },
    {
      "title": "Read and copy",
      "content": "The panel shows events for <strong>Me</strong> and <strong>Other</strong> as they happen. Use the copy button to put them on the clipboard and paste them into your message to us; the bin clears the list.",
      "screenshot": "03-logs-panel.png"
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "Reproduce the problem with logs on, then copy them straight away.",
      "Logs stay in memory only, up to the most recent 2,000 entries, and are cleared when you switch them off.",
      "Keys, tokens and email addresses are removed before anything is recorded, and audio is never stored."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "Where do I send the logs?",
        "answer": "Paste them into your feedback from the account menu, or into an email to support@kizuna.ai."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "诊断日志",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "诊断日志会记录会话期间 Sokuji 与翻译服务之间交换的内容。出现问题时，它们能帮助我们找到原因。诊断日志默认关闭。"
  },
  "steps": [
    {
      "title": "开启",
      "content": "在设置的<strong>帮助</strong>下，开启<strong>诊断日志</strong>。重启后它仍保持开启，直到你将其关闭。",
      "screenshot": "01-diagnostic-logs-switch.png"
    },
    {
      "title": "打开日志面板",
      "content": "标题栏中会出现<strong>日志</strong>按钮。",
      "screenshot": "02-logs-button.png"
    },
    {
      "title": "查看并复制",
      "content": "面板会实时显示<strong>我</strong>和<strong>对方</strong>的事件。使用复制按钮把它们复制到剪贴板，然后粘贴到你发给我们的消息中；垃圾桶按钮可清空列表。",
      "screenshot": "03-logs-panel.png"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "在开启日志的状态下重现问题，然后立即复制日志。",
      "日志只保存在内存中，最多保留最近的 2,000 条，关闭后会被清除。",
      "密钥、令牌和电子邮件地址会在记录之前被移除，音频也从不保存。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "日志应该发送到哪里？",
        "answer": "把它们粘贴到从账号菜单发送的反馈中，或粘贴到发给 support@kizuna.ai 的邮件中。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "診断ログ",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "診断ログには、セッション中に Sokuji と翻訳サービスの間でやり取りされた内容が記録されます。問題が起きたときに原因を突き止める手がかりになります。既定ではオフです。"
  },
  "steps": [
    {
      "title": "オンにする",
      "content": "設定の<strong>ヘルプ</strong>で<strong>診断ログ</strong>をオンにします。オフにするまで、再起動後もオンのままです。",
      "screenshot": "01-diagnostic-logs-switch.png"
    },
    {
      "title": "ログパネルを開く",
      "content": "タイトルバーに<strong>ログ</strong>ボタンが表示されます。",
      "screenshot": "02-logs-button.png"
    },
    {
      "title": "読んでコピーする",
      "content": "パネルには<strong>自分</strong>と<strong>相手</strong>のイベントがリアルタイムで表示されます。コピーボタンでクリップボードにコピーし、私たちへのメッセージに貼り付けてください。ゴミ箱ボタンで一覧をクリアできます。",
      "screenshot": "03-logs-panel.png"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "ログをオンにした状態で問題を再現し、すぐにコピーしてください。",
      "ログはメモリ上にのみ保持され、直近の最大 2,000 件までで、オフにすると消去されます。",
      "キー、トークン、メールアドレスは記録される前に取り除かれ、音声が保存されることはありません。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "ログはどこに送ればよいですか？",
        "answer": "アカウントメニューから送るフィードバックか、support@kizuna.ai 宛てのメールに貼り付けてください。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "진단 로그",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "진단 로그는 세션 중에 Sokuji와 번역 서비스가 주고받는 내용을 기록합니다. 문제가 생겼을 때 원인을 찾는 데 도움이 됩니다. 기본적으로 꺼져 있습니다."
  },
  "steps": [
    {
      "title": "켜기",
      "content": "설정의 <strong>도움말</strong>에서 <strong>진단 로그</strong>를 켜세요. 끌 때까지는 다시 시작한 후에도 켜진 상태로 유지됩니다.",
      "screenshot": "01-diagnostic-logs-switch.png"
    },
    {
      "title": "로그 패널 열기",
      "content": "제목 표시줄에 <strong>로그</strong> 버튼이 나타납니다.",
      "screenshot": "02-logs-button.png"
    },
    {
      "title": "읽고 복사하기",
      "content": "패널에는 <strong>나</strong>와 <strong>상대방</strong>의 이벤트가 실시간으로 표시됩니다. 복사 버튼으로 클립보드에 복사한 뒤 저희에게 보내는 메시지에 붙여 넣으세요. 휴지통 버튼은 목록을 지웁니다.",
      "screenshot": "03-logs-panel.png"
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "로그를 켠 상태에서 문제를 재현한 다음 바로 복사하세요.",
      "로그는 메모리에만 최근 최대 2,000개까지 보관되며, 끄면 지워집니다.",
      "키, 토큰, 이메일 주소는 기록되기 전에 제거되며, 오디오는 절대 저장되지 않습니다."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "로그는 어디로 보내나요?",
        "answer": "계정 메뉴에서 보내는 피드백이나 support@kizuna.ai로 보내는 이메일에 붙여 넣으세요."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Registros de diagnóstico",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "Los registros de diagnóstico recogen lo que Sokuji y el servicio de traducción intercambian durante una sesión. Nos ayudan a encontrar la causa cuando algo falla. Están desactivados de forma predeterminada."
  },
  "steps": [
    {
      "title": "Actívalos",
      "content": "En Configuración, en <strong>Ayuda</strong>, activa <strong>Registros de diagnóstico</strong>. Siguen activados después de reiniciar, hasta que los desactives.",
      "screenshot": "01-diagnostic-logs-switch.png"
    },
    {
      "title": "Abre el panel de registros",
      "content": "Aparece un botón <strong>Registros</strong> en la barra de título.",
      "screenshot": "02-logs-button.png"
    },
    {
      "title": "Lee y copia",
      "content": "El panel muestra los eventos de <strong>Yo</strong> y <strong>Otro</strong> a medida que ocurren. Usa el botón de copiar para ponerlos en el portapapeles y pegarlos en tu mensaje para nosotros; la papelera vacía la lista.",
      "screenshot": "03-logs-panel.png"
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "Reproduce el problema con los registros activados y cópialos enseguida.",
      "Los registros solo se guardan en memoria, hasta las 2000 entradas más recientes, y se borran al desactivarlos.",
      "Las claves, los tokens y las direcciones de correo electrónico se eliminan antes de registrar nada, y el audio nunca se guarda."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "¿Adónde envío los registros?",
        "answer": "Pégalos en tus comentarios desde el menú de la cuenta, o en un correo a support@kizuna.ai."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Journaux de diagnostic",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "Les journaux de diagnostic enregistrent ce que Sokuji et le service de traduction échangent pendant une session. Ils nous aident à trouver la cause d’un problème. Ils sont désactivés par défaut."
  },
  "steps": [
    {
      "title": "Activez-les",
      "content": "Dans les Paramètres, sous <strong>Aide</strong>, activez <strong>Journaux de diagnostic</strong>. Ils restent activés après un redémarrage, jusqu’à ce que vous les désactiviez.",
      "screenshot": "01-diagnostic-logs-switch.png"
    },
    {
      "title": "Ouvrez le panneau des journaux",
      "content": "Un bouton <strong>Journaux</strong> apparaît dans la barre de titre.",
      "screenshot": "02-logs-button.png"
    },
    {
      "title": "Lisez et copiez",
      "content": "Le panneau affiche en direct les événements de <strong>Moi</strong> et d’<strong>Autre</strong>. Utilisez le bouton de copie pour les placer dans le presse-papiers et les coller dans votre message ; la corbeille vide la liste.",
      "screenshot": "03-logs-panel.png"
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "Reproduisez le problème avec les journaux activés, puis copiez-les tout de suite.",
      "Les journaux restent uniquement en mémoire, jusqu’aux 2 000 entrées les plus récentes, et sont effacés quand vous les désactivez.",
      "Les clés, les jetons et les adresses e-mail sont supprimés avant tout enregistrement, et l’audio n’est jamais conservé."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "Où dois-je envoyer les journaux ?",
        "answer": "Collez-les dans votre retour envoyé depuis le menu du compte, ou dans un e-mail à support@kizuna.ai."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "سجلات التشخيص",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "تسجّل سجلات التشخيص ما يتبادله Sokuji وخدمة الترجمة أثناء الجلسة. تساعدنا على معرفة السبب عندما يحدث خطأ ما. تكون متوقفة افتراضيًا."
  },
  "steps": [
    {
      "title": "فعّلها",
      "content": "في الإعدادات، تحت <strong>مساعدة</strong>، فعّل <strong>سجلات التشخيص</strong>. تبقى مفعّلة بعد إعادة التشغيل إلى أن توقفها.",
      "screenshot": "01-diagnostic-logs-switch.png"
    },
    {
      "title": "افتح لوحة السجلات",
      "content": "يظهر زر <strong>السجلات</strong> في شريط العنوان.",
      "screenshot": "02-logs-button.png"
    },
    {
      "title": "اقرأ وانسخ",
      "content": "تعرض اللوحة أحداث <strong>أنا</strong> و<strong>الآخر</strong> فور حدوثها. استخدم زر النسخ لوضعها في الحافظة ولصقها في رسالتك إلينا؛ ويمسح زر سلة المهملات القائمة.",
      "screenshot": "03-logs-panel.png"
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "أعد إنتاج المشكلة والسجلات مفعّلة، ثم انسخها فورًا.",
      "تبقى السجلات في الذاكرة فقط، بحد أقصى أحدث 2,000 إدخال، وتُمسح عند إيقافها.",
      "تُزال المفاتيح والرموز المميزة (tokens) وعناوين البريد الإلكتروني قبل تسجيل أي شيء، ولا يُخزَّن الصوت أبدًا."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "إلى أين أرسل السجلات؟",
        "answer": "الصقها في ملاحظاتك المرسلة من قائمة الحساب، أو في رسالة بريد إلكتروني إلى support@kizuna.ai."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Logs de diagnóstico",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "Os logs de diagnóstico registram o que o Sokuji e o serviço de tradução trocam durante uma sessão. Eles nos ajudam a encontrar a causa quando algo dá errado. Vêm desativados por padrão."
  },
  "steps": [
    {
      "title": "Ative os logs",
      "content": "Em Configurações, em <strong>Ajuda</strong>, ative <strong>Logs de diagnóstico</strong>. Eles continuam ativados depois de reiniciar, até você desativá-los.",
      "screenshot": "01-diagnostic-logs-switch.png"
    },
    {
      "title": "Abra o painel de logs",
      "content": "Um botão <strong>Logs</strong> aparece na barra de título.",
      "screenshot": "02-logs-button.png"
    },
    {
      "title": "Leia e copie",
      "content": "O painel mostra os eventos de <strong>Eu</strong> e <strong>Outro</strong> à medida que acontecem. Use o botão de copiar para colocá-los na área de transferência e colá-los na sua mensagem para nós; a lixeira limpa a lista.",
      "screenshot": "03-logs-panel.png"
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "Reproduza o problema com os logs ativados e copie-os logo em seguida.",
      "Os logs ficam apenas na memória, até as 2.000 entradas mais recentes, e são apagados quando você os desativa.",
      "Chaves, tokens e endereços de e-mail são removidos antes de qualquer registro, e o áudio nunca é armazenado."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "Para onde envio os logs?",
        "answer": "Cole-os no seu feedback pelo menu da conta ou em um e-mail para support@kizuna.ai."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Диагностические журналы",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Диагностические журналы записывают, чем Sokuji и сервис перевода обмениваются во время сеанса. Они помогают нам найти причину, когда что-то идёт не так. По умолчанию они выключены."
  },
  "steps": [
    {
      "title": "Включите их",
      "content": "В настройках, в разделе <strong>Помощь</strong>, включите <strong>Диагностические журналы</strong>. Они остаются включёнными и после перезапуска, пока вы их не выключите.",
      "screenshot": "01-diagnostic-logs-switch.png"
    },
    {
      "title": "Откройте панель логов",
      "content": "В строке заголовка появляется кнопка <strong>Логи</strong>.",
      "screenshot": "02-logs-button.png"
    },
    {
      "title": "Читайте и копируйте",
      "content": "Панель показывает события для <strong>Я</strong> и <strong>Собеседник</strong> по мере их появления. Кнопкой копирования поместите их в буфер обмена и вставьте в своё сообщение нам; кнопка с корзиной очищает список.",
      "screenshot": "03-logs-panel.png"
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "Воспроизведите проблему с включёнными журналами и сразу скопируйте их.",
      "Журналы хранятся только в памяти, не более 2 000 последних записей, и удаляются, когда вы их выключаете.",
      "Ключи, токены и адреса электронной почты удаляются перед записью, а аудио никогда не сохраняется."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Куда отправлять журналы?",
        "answer": "Вставьте их в отзыв через меню учётной записи или в письмо на support@kizuna.ai."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Diagnoseprotokolle",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Diagnoseprotokolle zeichnen auf, was Sokuji und der Übersetzungsdienst während einer Sitzung austauschen. Sie helfen uns, die Ursache zu finden, wenn etwas schiefgeht. Standardmäßig sind sie ausgeschaltet."
  },
  "steps": [
    {
      "title": "Einschalten",
      "content": "Schalten Sie in den Einstellungen unter <strong>Hilfe</strong> die <strong>Diagnoseprotokolle</strong> ein. Sie bleiben auch nach einem Neustart eingeschaltet, bis Sie sie ausschalten.",
      "screenshot": "01-diagnostic-logs-switch.png"
    },
    {
      "title": "Das Protokollfenster öffnen",
      "content": "In der Titelleiste erscheint eine Schaltfläche <strong>Protokolle</strong>.",
      "screenshot": "02-logs-button.png"
    },
    {
      "title": "Lesen und kopieren",
      "content": "Das Fenster zeigt Ereignisse für <strong>Ich</strong> und <strong>Gegenüber</strong>, sobald sie auftreten. Mit der Kopieren-Schaltfläche legen Sie sie in die Zwischenablage und fügen sie in Ihre Nachricht an uns ein; der Papierkorb leert die Liste.",
      "screenshot": "03-logs-panel.png"
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Reproduzieren Sie das Problem mit eingeschalteten Protokollen und kopieren Sie sie dann sofort.",
      "Protokolle werden nur im Arbeitsspeicher gehalten, höchstens die letzten 2.000 Einträge, und beim Ausschalten gelöscht.",
      "Schlüssel, Tokens und E-Mail-Adressen werden vor dem Aufzeichnen entfernt, und Audio wird nie gespeichert."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Wohin schicke ich die Protokolle?",
        "answer": "Fügen Sie sie in Ihr Feedback über das Kontomenü oder in eine E-Mail an support@kizuna.ai ein."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Діагностичні журнали",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Діагностичні журнали записують, чим Sokuji і сервіс перекладу обмінюються під час сеансу. Вони допомагають нам знайти причину, коли щось іде не так. За замовчуванням їх вимкнено."
  },
  "steps": [
    {
      "title": "Увімкніть їх",
      "content": "У налаштуваннях, у розділі <strong>Допомога</strong>, увімкніть <strong>Діагностичні журнали</strong>. Вони залишаються ввімкненими й після перезапуску, доки ви їх не вимкнете.",
      "screenshot": "01-diagnostic-logs-switch.png"
    },
    {
      "title": "Відкрийте панель логів",
      "content": "У рядку заголовка з’являється кнопка <strong>Логи</strong>.",
      "screenshot": "02-logs-button.png"
    },
    {
      "title": "Читайте й копіюйте",
      "content": "Панель показує події для <strong>Я</strong> і <strong>Співрозмовник</strong> у міру їх появи. Кнопкою копіювання помістіть їх у буфер обміну та вставте у своє повідомлення нам; кнопка з кошиком очищає список.",
      "screenshot": "03-logs-panel.png"
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Відтворіть проблему з увімкненими журналами й одразу скопіюйте їх.",
      "Журнали зберігаються лише в пам’яті, не більше 2 000 останніх записів, і видаляються, коли ви їх вимикаєте.",
      "Ключі, токени й адреси електронної пошти видаляються перед записом, а аудіо ніколи не зберігається."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Куди надсилати журнали?",
        "answer": "Вставте їх у відгук через меню облікового запису або в лист на support@kizuna.ai."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Log di diagnostica",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "I log di diagnostica registrano ciò che Sokuji e il servizio di traduzione si scambiano durante una sessione. Ci aiutano a trovare la causa quando qualcosa non va. Sono disattivati per impostazione predefinita."
  },
  "steps": [
    {
      "title": "Attivali",
      "content": "In Impostazioni, sotto <strong>Aiuto</strong>, attiva <strong>Log di diagnostica</strong>. Restano attivi anche dopo un riavvio, finché non li disattivi.",
      "screenshot": "01-diagnostic-logs-switch.png"
    },
    {
      "title": "Apri il pannello Log",
      "content": "Nella barra del titolo compare un pulsante <strong>Log</strong>.",
      "screenshot": "02-logs-button.png"
    },
    {
      "title": "Leggi e copia",
      "content": "Il pannello mostra gli eventi di <strong>Io</strong> e <strong>Altro</strong> man mano che avvengono. Usa il pulsante di copia per metterli negli appunti e incollarli nel tuo messaggio per noi; il cestino svuota l’elenco.",
      "screenshot": "03-logs-panel.png"
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Riproduci il problema con i log attivi, poi copiali subito.",
      "I log restano solo in memoria, fino alle 2.000 voci più recenti, e vengono cancellati quando li disattivi.",
      "Chiavi, token e indirizzi email vengono rimossi prima di qualsiasi registrazione, e l’audio non viene mai memorizzato."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Dove invio i log?",
        "answer": "Incollali nel feedback dal menu dell’account, oppure in un’email a support@kizuna.ai."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeatureDiagnosticLogs() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/diagnostic-logs" />;
}
